import { useEffect, useRef, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { decodeBarcodeFromImage } from "../lib/lookupBarcode";
import { decodeImageFileWithZxing, createZxingLiveScanner } from "../lib/zxing";

// Layered barcode reader — best signal first:
//
//   LIVE
//     1a. Native BarcodeDetector (Chrome/Edge/Android, Safari 17+).
//     1b. @zxing/browser on the same <video> stream when native is
//         absent. Lazy-loaded ~200KB; free, offline.
//
//   PHOTO (iOS PWA standalone where live video is blocked)
//     2a. @zxing/browser on a still image from <input type="file"
//         capture="environment">.
//     2b. Claude vision edge fn (decode-barcode-image) when zxing
//         can't read damaged / faded codes.
//
//   TYPED
//     3. User reads the digits and types them.
//
// Emits onDetected(barcode) once per session by default; the stream
// stops on the first read. mode="rapid" keeps the stream open for
// shop-mode-style sweeps and applies same-UPC + global-cooldown
// suppression so a paused barcode under the camera doesn't double-fire.

const BARCODE_FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "itf"];
const SAME_UPC_SUPPRESSION_MS = 10000;
const GLOBAL_COOLDOWN_MS      = 10000;
export { GLOBAL_COOLDOWN_MS as SCANNER_COOLDOWN_MS };

export default function BarcodeScanner({
  onDetected, onCancel,
  mode = "single", embedded = false, paused = false,
}) {
  const rapidMode = mode === "rapid";
  const lastRapidRef    = useRef({ upc: "", at: 0 });
  const lastAnyScanRef  = useRef(0);
  const pausedRef       = useRef(paused);
  const videoRef        = useRef(null);
  const streamRef       = useRef(null);
  const detectorRef     = useRef(null);
  const tickRef         = useRef(null);
  const photoInputRef   = useRef(null);
  const cancelledRef    = useRef(false);
  const zxingStopRef    = useRef(null);
  const videoTrackRef   = useRef(null);
  const struggleTimerRef = useRef(null);

  // Phase machine — three top-level surfaces. `live` shows the camera;
  // `verify` holds digits that came back from photo decode and asks
  // the user to confirm them before we hit the lookup; `manual` is
  // the no-camera path (camera denied, native unavailable, error).
  // Booting flickers as `live` with no video frames; we don't block
  // on a separate `init` state so the scanner feels instant.
  const [phase, setPhase]       = useState("live");
  const [errMsg, setErrMsg]     = useState("");
  const [typed, setTyped]       = useState("");
  const [decoding, setDecoding] = useState(false);
  const [decodeMsg, setDecodeMsg] = useState("");
  const [verifyDigits, setVerifyDigits] = useState("");
  const [manualReason, setManualReason] = useState("");

  // Capability state — set after the video track lands. Null/false
  // means "not supported on this device", and the corresponding
  // affordance simply doesn't render.
  const [zoomCaps,       setZoomCaps]       = useState(null);
  const [zoomValue,      setZoomValue]      = useState(1);
  const [zoomExpanded,   setZoomExpanded]   = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [torchOn,        setTorchOn]        = useState(false);
  const [strugglingNudge, setStrugglingNudge] = useState(false);
  // Success flash — when a UPC lands we briefly tint the reticle
  // green + pulse so the user gets unambiguous confirmation the
  // scanner caught it before the stream tears down. Held in state
  // so the brief flash can finish even after onDetected fires.
  const [caughtDigits,  setCaughtDigits]  = useState("");
  const flashTimerRef = useRef(null);

  // Live-scanner boot. Camera first, then pick the best decoder we
  // can; native BarcodeDetector if available (zero-cost), else
  // lazy-load @zxing/browser. On hard camera denial fall through to
  // the manual surface so the typed entry path is still useful.
  const bootCamera = async () => {
    cancelledRef.current = false;
    stopStream();
    setPhase("live");
    setStrugglingNudge(false);

    let stream;
    try {
      // Resolution + framerate trimmed 2026-05-06 to fix
      // overheating in Shop Mode. 1920×1080 @ 30fps was
      // ~3× more pixels per frame than barcode decoders
      // need (UPC digits are easily resolved at 720p), and
      // 30fps was 2× more than the ~4Hz polling cadence
      // can consume. The iSight camera ISP and the JS
      // decoder both burn way less power at 1280×720 @ 15fps,
      // and detection accuracy is unchanged for any normal
      // hand-held distance (15-25cm from the barcode).
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width:      { ideal: 1280 },
          height:     { ideal: 720 },
          focusMode:  { ideal: "continuous" },
          frameRate:  { ideal: 15, max: 24 },
        },
        audio: false,
      });
    } catch (e) {
      console.warn("[barcode] camera grant failed:", e);
      if (!cancelledRef.current) {
        setManualReason("denied");
        setPhase("manual");
      }
      return;
    }
    if (cancelledRef.current) {
      stream.getTracks().forEach(t => t.stop());
      return;
    }
    streamRef.current = stream;
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      try { await videoRef.current.play(); } catch { /* autoplay blocked */ }
    }

    // Probe track capabilities for zoom + torch. Silently skip when
    // absent; the core scanning path doesn't need them.
    try {
      const track = stream.getVideoTracks?.()[0];
      if (track && typeof track.getCapabilities === "function") {
        const caps = track.getCapabilities();
        if (caps?.zoom && typeof caps.zoom.min === "number") {
          setZoomCaps({ min: caps.zoom.min, max: caps.zoom.max, step: caps.zoom.step || 0.1 });
          // Default to 2× (clamped) — a wide-angle phone lens at 1×
          // flattens small UPCs into low-contrast smudges; 2× gives
          // the decoder more pixels per bar without forcing the user
          // to adjust.
          const target = Math.max(caps.zoom.min, Math.min(caps.zoom.max, 2));
          setZoomValue(target);
          videoTrackRef.current = track;
          track.applyConstraints({ advanced: [{ zoom: target }] })
            .catch(e => console.warn("[barcode] initial zoom apply failed:", e));
        }
        if (caps?.torch) {
          setTorchSupported(true);
          videoTrackRef.current = track;
        }
      }
    } catch (e) {
      console.warn("[barcode] track caps probe failed:", e);
    }

    // Struggling-nudge timer. After 8s with no hit the live hint
    // swaps to a "try a photo instead" prompt — the most common
    // failure mode is small barcodes that the phone can't focus
    // close enough to decode live.
    if (struggleTimerRef.current) window.clearTimeout(struggleTimerRef.current);
    struggleTimerRef.current = window.setTimeout(() => {
      if (!cancelledRef.current) setStrugglingNudge(true);
    }, 8000);

    const hasNative = typeof window !== "undefined" && "BarcodeDetector" in window;
    if (hasNative) {
      try {
        const supported = await window.BarcodeDetector.getSupportedFormats();
        const formats = BARCODE_FORMATS.filter(f => supported.includes(f));
        if (formats.length > 0) {
          detectorRef.current = new window.BarcodeDetector({ formats });
          startNativePolling();
          return;
        }
      } catch (e) {
        console.warn("[barcode] native detector init failed, falling back to zxing:", e);
      }
    }

    try {
      const scanner = await createZxingLiveScanner(
        videoRef.current,
        (digits) => {
          if (cancelledRef.current) return;
          if (rapidMode) {
            try { navigator.vibrate?.(12); } catch { /* not supported */ }
            onDetected?.(digits);
            return;
          }
          fireCatch(digits);
        },
        (err) => { console.warn("[barcode] zxing live error:", err); },
        { continuous: rapidMode, isPaused: () => pausedRef.current },
      );
      zxingStopRef.current = scanner;
    } catch (err) {
      console.warn("[barcode] zxing fallback failed:", err);
      stopStream();
      if (!cancelledRef.current) {
        setManualReason("unavailable");
        setPhase("manual");
      }
    }
  };

  // Native BarcodeDetector polling loop. Zxing manages its own loop.
  // Single-mode catch — runs the success flash + haptic, then hands
  // the digits up to the caller. Defers onDetected by ~280ms so the
  // user sees the green pulse on the reticle before the parent tears
  // the scanner down. Rapid mode bypasses this (it's mid-sweep, the
  // user isn't looking at the reticle for confirmation).
  function fireCatch(digits) {
    setCaughtDigits(digits);
    try { navigator.vibrate?.(18); } catch { /* not supported */ }
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => {
      stopStream();
      if (!cancelledRef.current) onDetected?.(digits);
    }, 280);
  }

  function startNativePolling() {
    const tick = async () => {
      if (cancelledRef.current) return;
      if (pausedRef.current) {
        tickRef.current = window.setTimeout(tick, 350);
        return;
      }
      const video = videoRef.current;
      const detector = detectorRef.current;
      if (video && detector && video.readyState >= 2) {
        try {
          const codes = await detector.detect(video);
          if (codes && codes.length > 0) {
            const raw = String(codes[0].rawValue || "").trim();
            if (/^\d{8,14}$/.test(raw)) {
              if (rapidMode) {
                const now = Date.now();
                const sameUpcRecent = raw === lastRapidRef.current.upc
                  && (now - lastRapidRef.current.at) < SAME_UPC_SUPPRESSION_MS;
                const cooldownActive = (now - lastAnyScanRef.current) < GLOBAL_COOLDOWN_MS;
                if (!sameUpcRecent && !cooldownActive) {
                  lastRapidRef.current = { upc: raw, at: now };
                  lastAnyScanRef.current = now;
                  try { navigator.vibrate?.(12); } catch { /* not supported */ }
                  if (!cancelledRef.current) onDetected?.(raw);
                }
                tickRef.current = window.setTimeout(tick, 350);
                return;
              }
              fireCatch(raw);
              return;
            }
          }
        } catch {
          // Detector occasionally throws on a corrupted frame.
        }
      }
      tickRef.current = window.setTimeout(tick, 350);
    };
    tickRef.current = window.setTimeout(tick, 350);
  }

  useEffect(() => {
    bootCamera();
    return () => {
      cancelledRef.current = true;
      stopStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { pausedRef.current = paused; }, [paused]);

  function stopStream() {
    if (tickRef.current) {
      window.clearTimeout(tickRef.current);
      tickRef.current = null;
    }
    if (zxingStopRef.current) {
      try { zxingStopRef.current.stop?.(); } catch { /* noop */ }
      zxingStopRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    videoTrackRef.current = null;
    setZoomCaps(null);
    setZoomValue(1);
    setZoomExpanded(false);
    setTorchSupported(false);
    setTorchOn(false);
    if (struggleTimerRef.current) {
      window.clearTimeout(struggleTimerRef.current);
      struggleTimerRef.current = null;
    }
    if (flashTimerRef.current) {
      window.clearTimeout(flashTimerRef.current);
      flashTimerRef.current = null;
    }
    setStrugglingNudge(false);
    setCaughtDigits("");
  }

  const applyZoom = async (next) => {
    const track = videoTrackRef.current;
    if (!track || !zoomCaps) return;
    const clamped = Math.max(zoomCaps.min, Math.min(zoomCaps.max, Number(next)));
    setZoomValue(clamped);
    try {
      await track.applyConstraints({ advanced: [{ zoom: clamped }] });
    } catch (e) {
      console.warn("[barcode] zoom apply failed:", e);
    }
  };

  const toggleTorch = async () => {
    const track = videoTrackRef.current;
    if (!track || !torchSupported) return;
    const next = !torchOn;
    try {
      await track.applyConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (e) {
      console.warn("[barcode] torch apply failed:", e);
    }
  };

  function handleManualSubmit(e) {
    e?.preventDefault?.();
    const clean = typed.trim();
    if (!/^\d{8,14}$/.test(clean)) {
      setErrMsg("Barcode must be 8–14 digits.");
      return;
    }
    stopStream();
    onDetected?.(clean);
  }

  function handleVerifySubmit(e) {
    e?.preventDefault?.();
    const clean = verifyDigits.trim();
    if (!/^\d{8,14}$/.test(clean)) {
      setErrMsg("Barcode must be 8–14 digits.");
      return;
    }
    stopStream();
    onDetected?.(clean);
  }

  // Photo-capture path — works on every iOS PWA where live decode
  // is blocked. Try zxing first (algorithmic, free, more accurate),
  // then fall through to Claude vision (digit OCR) for damaged
  // codes. Land on the verify surface so the user can eyeball
  // before we hit OFF.
  async function handlePhotoCapture(e) {
    const file = e?.target?.files?.[0];
    if (!file) return;
    if (photoInputRef.current) photoInputRef.current.value = "";
    const mediaType = file.type && /^image\/(jpeg|png|webp)$/.test(file.type)
      ? file.type
      : "image/jpeg";
    setDecoding(true);
    setDecodeMsg("Reading the barcode…");
    try {
      const zxingRes = await decodeImageFileWithZxing(file);
      if (zxingRes?.found && zxingRes.barcode) {
        stopStream();
        setVerifyDigits(zxingRes.barcode);
        setDecodeMsg("");
        setPhase("verify");
        return;
      }

      const base64 = await fileToBase64(file);
      const res = await decodeBarcodeFromImage(base64, mediaType);
      if (res?.found && res.barcode) {
        stopStream();
        setVerifyDigits(res.barcode);
        setDecodeMsg("");
        setPhase("verify");
        return;
      }
      const reason =
        res?.reason === "no_barcode_visible" ? "No barcode in the photo. Retake with the barcode bigger in frame."
        : res?.reason === "not_a_product"    ? "That looks like a QR code, not a product barcode."
        : res?.reason === "unreadable"       ? "Too blurry or cut off. Hold steady, fill the frame, plenty of light."
        : res?.reason === "edge_fn_not_deployed" ? "Decode service offline. Type the digits below."
        : res?.reason === "decode_failed"    ? "Decode service errored. Type the digits below."
        : res?.reason === "empty_response"   ? "Decode service returned nothing. Try again."
        : "Couldn't read that photo. Try again or type the digits.";
      setDecodeMsg(reason);
    } catch (err) {
      console.error("[barcode] photo decode failed:", err);
      setDecodeMsg("Photo decode failed. Try again or type the digits.");
    } finally {
      setDecoding(false);
    }
  }

  function openPhotoCapture() {
    if (photoInputRef.current) photoInputRef.current.click();
  }

  return (
    <div style={shellStyle(embedded)}>
      {/* TOP BAR — single dismiss, safe-area top.
          Embedded mode (ShopMode) uses a tighter top edge since the
          host already owns the surrounding chrome. */}
      <div style={topBarStyle(embedded)}>
        <button
          type="button"
          onClick={() => { stopStream(); onCancel?.(); }}
          aria-label="Close scanner"
          className="mcm-focusable"
          style={dismissBtnStyle}
        >
          <CloseGlyph />
        </button>
        <div style={topBarTitleStyle}>SCAN BARCODE</div>
        <div style={{ width: 36 }} />
      </div>

      {/* CAMERA SURFACE — always rendered while phase === "live" so
          the stream has a sink. In verify/manual we hide it visually
          but leave it in the tree so the camera doesn't have to
          re-boot if the user backs out. */}
      <div style={cameraAreaStyle(phase === "live")}>
        <video
          ref={videoRef}
          playsInline
          muted
          style={videoStyle}
        />
        {phase === "live" && <Reticle caught={caughtDigits} />}

        {/* Right-edge control cluster — torch + photo. Single column
            so the eye doesn't have to track multiple zones. */}
        {phase === "live" && (
          <div style={controlClusterStyle}>
            {torchSupported && (
              <CircleControl
                onClick={toggleTorch}
                active={torchOn}
                glyph={<TorchGlyph on={torchOn} />}
                label={torchOn ? "Torch on" : "Torch off"}
              />
            )}
            {zoomCaps && (
              <CircleControl
                onClick={() => setZoomExpanded(v => !v)}
                active={zoomExpanded}
                glyph={<ZoomGlyph />}
                label={`Zoom ${zoomValue.toFixed(1)}×`}
              />
            )}
            <CircleControl
              onClick={openPhotoCapture}
              active={false}
              glyph={<PhotoGlyph />}
              label="Take a photo instead"
            />
          </div>
        )}

        {/* Zoom slider — collapsed by default; expanded when the user
            taps the zoom control. Reads natural without consuming
            permanent UI real estate. */}
        {phase === "live" && zoomCaps && zoomExpanded && (
          <div style={zoomTrayStyle}>
            <input
              type="range"
              min={zoomCaps.min}
              max={zoomCaps.max}
              step={zoomCaps.step}
              value={zoomValue}
              onChange={(e) => applyZoom(Number(e.target.value))}
              style={{ flex: 1, accentColor: "#FFFFFF" }}
            />
            <span style={zoomReadoutStyle}>{zoomValue.toFixed(1)}×</span>
          </div>
        )}

        {/* Bottom hint — single line, fades to the struggling-nudge
            copy after 8s of no hits. The nudge replaces the hint
            in-place so the bottom edge doesn't grow new chrome. */}
        {phase === "live" && (
          <div style={hintStyle}>
            {strugglingNudge
              ? <button type="button" onClick={openPhotoCapture} style={hintNudgeBtnStyle} className="mcm-focusable">
                  Small barcode? Tap to snap a photo instead.
                </button>
              : "Point at the barcode on the package"}
          </div>
        )}
      </div>

      {/* VERIFY — photo decoded a UPC, user confirms before lookup.
          Compact card centered on the black backdrop, no scrolling. */}
      {phase === "verify" && (
        <CenteredCard>
          <CardKicker>VERIFY</CardKicker>
          <CardTitle>Does this match the digits on the package?</CardTitle>
          <form onSubmit={handleVerifySubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input
              autoFocus
              type="text"
              inputMode="numeric"
              pattern="\d*"
              value={verifyDigits}
              onChange={(e) => { setVerifyDigits(e.target.value.replace(/\D/g, "")); setErrMsg(""); }}
              maxLength={14}
              style={digitsInputStyle}
            />
            <div style={digitsHintStyle}>{formatBarcodeDigits(verifyDigits) || "—"}</div>
            {errMsg && <div style={errMsgStyle}>{errMsg}</div>}
            <button type="submit" disabled={!verifyDigits} style={primaryBtnStyle(!!verifyDigits)} className="mcm-focusable">
              LOOK UP
            </button>
            <button
              type="button"
              onClick={() => { setPhase("live"); setVerifyDigits(""); setErrMsg(""); bootCamera(); }}
              style={ghostBtnStyle}
              className="mcm-focusable"
            >
              Retake photo
            </button>
          </form>
        </CenteredCard>
      )}

      {/* MANUAL — camera unavailable. Single compact card: short
          headline, one CTA (photo), one input (typed). No scrolling
          wall of browser-specific instructions. */}
      {phase === "manual" && (
        <CenteredCard>
          <CardKicker>{manualReason === "denied" ? "CAMERA BLOCKED" : "TYPE IT IN"}</CardKicker>
          <CardTitle>
            {manualReason === "denied"
              ? "Snap a photo or type the digits."
              : "Snap a photo or type the digits."}
          </CardTitle>
          <button
            type="button"
            onClick={openPhotoCapture}
            disabled={decoding}
            style={primaryBtnStyle(!decoding)}
            className="mcm-focusable"
          >
            {decoding ? "READING…" : "TAKE A PHOTO"}
          </button>
          {decodeMsg && !decoding && (
            <div style={decodeMsgStyle}>{decodeMsg}</div>
          )}
          <form onSubmit={handleManualSubmit} style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
            <div style={inputKickerStyle}>OR TYPE THE BARCODE</div>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              value={typed}
              onChange={(e) => { setTyped(e.target.value.replace(/\D/g, "")); setErrMsg(""); }}
              placeholder="e.g. 3017620422003"
              maxLength={14}
              style={digitsInputStyle}
            />
            {errMsg && <div style={errMsgStyle}>{errMsg}</div>}
            <button type="submit" disabled={!typed} style={primaryBtnStyle(!!typed)} className="mcm-focusable">
              LOOK UP
            </button>
            {manualReason === "denied" && (
              <button
                type="button"
                onClick={bootCamera}
                style={ghostBtnStyle}
                className="mcm-focusable"
              >
                Try the camera again
              </button>
            )}
          </form>
        </CenteredCard>
      )}

      {/* Hidden file input — single instance, shared by every photo
          trigger. The two-input bug in the old version (one in the
          live area, one in the manual area) silently dropped photo
          decode on whichever ref lost the assignment race. */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        onChange={handlePhotoCapture}
        style={{ display: "none" }}
      />
    </div>
  );
}

// ── Layout primitives ───────────────────────────────────────────────

function shellStyle(embedded) {
  if (embedded) {
    return {
      position: "relative",
      width: "100%", height: "100%",
      background: "#000",
      display: "flex", flexDirection: "column",
      overflow: "hidden",
    };
  }
  return {
    position: "fixed", inset: 0, zIndex: 348,
    background: "#000",
    display: "flex", flexDirection: "column",
  };
}

function topBarStyle(embedded) {
  return {
    flexShrink: 0,
    paddingTop: embedded ? 12 : "calc(12px + env(safe-area-inset-top))",
    paddingBottom: 12,
    paddingLeft: 16, paddingRight: 16,
    display: "flex", alignItems: "center", gap: 12,
    background: "#000",
  };
}

const dismissBtnStyle = {
  width: 36, height: 36,
  borderRadius: 18,
  background: "rgba(255,255,255,0.10)",
  border: "1px solid rgba(255,255,255,0.18)",
  color: "#FFFFFF",
  display: "flex", alignItems: "center", justifyContent: "center",
  cursor: "pointer",
  padding: 0,
  touchAction: "manipulation",
};

const topBarTitleStyle = {
  flex: 1,
  fontFamily: FONT.forma,
  fontSize: 11,
  fontWeight: 600,
  color: "rgba(255,255,255,0.62)",
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  textAlign: "center",
};

function cameraAreaStyle(visible) {
  return {
    position: "relative",
    flex: 1,
    minHeight: 0,
    background: "#000",
    overflow: "hidden",
    visibility: visible ? "visible" : "hidden",
  };
}

const videoStyle = {
  width: "100%", height: "100%",
  objectFit: "cover",
  display: "block",
};

function Reticle({ caught }) {
  // GREEN — MCM teal. The detector landed; corner brackets, scan
  // line, and a centered digit overlay all switch to this tint to
  // confirm the catch before the parent unmounts the scanner.
  const isCaught = !!caught;
  const color = isCaught ? "rgba(126,255,180,0.95)" : "rgba(255,255,255,0.92)";
  return (
    <div style={{
      position: "absolute", inset: 0,
      pointerEvents: "none",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        position: "relative",
        width: "78%", maxWidth: 380, height: 110,
        // Soft halo on catch — radial green glow that reads as
        // "the reticle just lit up" without overpowering the camera
        // feed. The 220ms ease lets it land hand-in-hand with the
        // 280ms onDetected delay in fireCatch().
        boxShadow: isCaught
          ? "0 0 0 9999px rgba(0,0,0,0.55), 0 0 60px 8px rgba(126,255,180,0.45)"
          : "0 0 0 9999px rgba(0,0,0,0.30)",
        transition: "box-shadow 220ms ease",
        borderRadius: 6,
      }}>
        <Corner placement={{ top: 0, left: 0 }}     edges="tl" tint={color} />
        <Corner placement={{ top: 0, right: 0 }}    edges="tr" tint={color} />
        <Corner placement={{ bottom: 0, left: 0 }}  edges="bl" tint={color} />
        <Corner placement={{ bottom: 0, right: 0 }} edges="br" tint={color} />
        {!isCaught && (
          <div style={{
            position: "absolute", left: 6, right: 6, top: "50%",
            height: 2,
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.85), transparent)",
            borderRadius: 2,
            animation: "barcode-scan-sweep 1.6s ease-in-out infinite",
          }} />
        )}
        {isCaught && (
          // Detected digits overlay — confirms WHAT was caught, not
          // just that something was. Pulse keyframe punches in once
          // and settles so the eye latches onto the number.
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            animation: "barcode-catch-pulse 280ms cubic-bezier(0.2,0.9,0.3,1.4) both",
          }}>
            <div style={{
              padding: "4px 14px",
              background: "rgba(0,0,0,0.55)",
              borderRadius: 4,
              fontFamily: "'forma-djr-deck', system-ui, sans-serif",
              fontSize: 16, fontWeight: 700,
              color: "#FFFFFF",
              letterSpacing: "0.14em",
              textShadow: "0 1px 4px rgba(0,0,0,0.6)",
            }}>
              {formatBarcodeDigits(caught)}
            </div>
          </div>
        )}
        <style>{`
          @keyframes barcode-scan-sweep {
            0%, 100% { transform: translateY(-32px); opacity: 0.4; }
            50%      { transform: translateY( 32px); opacity: 1; }
          }
          @keyframes barcode-catch-pulse {
            0%   { transform: scale(0.85); opacity: 0; }
            60%  { transform: scale(1.05); opacity: 1; }
            100% { transform: scale(1.00); opacity: 1; }
          }
        `}</style>
      </div>
    </div>
  );
}

function Corner({ placement, edges, tint = "rgba(255,255,255,0.92)" }) {
  const size = 22;
  const stroke = 2;
  const style = {
    position: "absolute",
    width: size, height: size,
    transition: "border-color 200ms ease",
    ...placement,
  };
  if (edges === "tl") { style.borderTop = `${stroke}px solid ${tint}`; style.borderLeft  = `${stroke}px solid ${tint}`; style.borderTopLeftRadius     = 6; }
  if (edges === "tr") { style.borderTop = `${stroke}px solid ${tint}`; style.borderRight = `${stroke}px solid ${tint}`; style.borderTopRightRadius    = 6; }
  if (edges === "bl") { style.borderBottom = `${stroke}px solid ${tint}`; style.borderLeft  = `${stroke}px solid ${tint}`; style.borderBottomLeftRadius  = 6; }
  if (edges === "br") { style.borderBottom = `${stroke}px solid ${tint}`; style.borderRight = `${stroke}px solid ${tint}`; style.borderBottomRightRadius = 6; }
  return <div style={style} />;
}

const controlClusterStyle = {
  position: "absolute",
  top: 16, right: 16,
  display: "flex", flexDirection: "column", gap: 10,
};

function CircleControl({ onClick, active, glyph, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="mcm-focusable"
      style={{
        width: 44, height: 44,
        borderRadius: 22,
        background: active ? "#FFFFFF" : "rgba(0,0,0,0.55)",
        border: active ? "1px solid #FFFFFF" : "1px solid rgba(255,255,255,0.22)",
        color: active ? "#1E1E1E" : "#FFFFFF",
        cursor: "pointer",
        padding: 0,
        display: "flex", alignItems: "center", justifyContent: "center",
        touchAction: "manipulation",
        boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
      }}
    >
      {glyph}
    </button>
  );
}

const zoomTrayStyle = {
  position: "absolute",
  left: 16, right: 76, bottom: 64,
  display: "flex", alignItems: "center", gap: 12,
  padding: "10px 14px",
  background: "rgba(0,0,0,0.55)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 22,
};

const zoomReadoutStyle = {
  fontFamily: FONT.forma,
  fontSize: 11,
  fontWeight: 600,
  color: "rgba(255,255,255,0.92)",
  minWidth: 36, textAlign: "right",
  letterSpacing: "0.04em",
};

const hintStyle = {
  position: "absolute",
  left: 16, right: 16,
  bottom: "calc(16px + env(safe-area-inset-bottom))",
  textAlign: "center",
  fontFamily: FONT.forma,
  fontSize: 13,
  fontWeight: 500,
  color: "rgba(255,255,255,0.85)",
  textShadow: "0 1px 3px rgba(0,0,0,0.7)",
};

const hintNudgeBtnStyle = {
  appearance: "none",
  border: "none",
  background: "rgba(255,255,255,0.92)",
  color: "#1E1E1E",
  fontFamily: FONT.forma,
  fontSize: 13,
  fontWeight: 600,
  padding: "10px 14px",
  borderRadius: 22,
  cursor: "pointer",
  touchAction: "manipulation",
  boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
};

// ── Centered card (verify + manual) ─────────────────────────────────
// Compact, fits within the camera area's flex column without
// scrolling. Sits in front of the (hidden) camera surface so the
// black backdrop reads as one continuous shell.
function CenteredCard({ children }) {
  return (
    <div style={{
      position: "absolute", inset: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "16px",
      pointerEvents: "auto",
    }}>
      <div style={{
        width: "100%",
        maxWidth: 360,
        padding: "22px 22px 24px",
        background: "rgba(20,20,20,0.92)",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 18,
        boxShadow: "0 18px 40px rgba(0,0,0,0.45)",
        display: "flex", flexDirection: "column", gap: 14,
      }}>
        {children}
      </div>
    </div>
  );
}

function CardKicker({ children }) {
  return (
    <div style={{
      fontFamily: FONT.forma,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.20em",
      color: "rgba(255,255,255,0.55)",
      textTransform: "uppercase",
    }}>
      {children}
    </div>
  );
}

function CardTitle({ children }) {
  return (
    <div style={{
      fontFamily: FONT.detail,
      fontStyle: "italic",
      fontWeight: 500,
      fontSize: 20,
      lineHeight: 1.25,
      color: "#F4EFE6",
    }}>
      {children}
    </div>
  );
}

const digitsInputStyle = {
  width: "100%",
  padding: "14px 16px",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 12,
  color: "#F4EFE6",
  boxSizing: "border-box",
  fontFamily: FONT.forma,
  fontSize: 18,
  letterSpacing: "0.08em",
  outline: "none",
  WebkitAppearance: "none",
};

const digitsHintStyle = {
  fontFamily: FONT.forma,
  fontSize: 11,
  fontWeight: 500,
  color: "rgba(255,255,255,0.45)",
  letterSpacing: "0.16em",
  textAlign: "center",
};

const inputKickerStyle = {
  fontFamily: FONT.forma,
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: "0.18em",
  color: "rgba(255,255,255,0.45)",
  textTransform: "uppercase",
};

const errMsgStyle = {
  fontFamily: FONT.forma,
  fontSize: 12,
  fontWeight: 500,
  color: COLOR.rose,
};

const decodeMsgStyle = {
  fontFamily: FONT.forma,
  fontSize: 12,
  fontWeight: 500,
  color: "rgba(255,255,255,0.75)",
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 10,
  padding: "10px 12px",
  lineHeight: 1.45,
};

function primaryBtnStyle(enabled) {
  return {
    width: "100%",
    padding: "14px",
    background: enabled ? "#FFFFFF" : "rgba(255,255,255,0.10)",
    color:      enabled ? "#1E1E1E" : "rgba(255,255,255,0.40)",
    border: "none",
    borderRadius: 12,
    fontFamily: FONT.forma,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    cursor: enabled ? "pointer" : "not-allowed",
    touchAction: "manipulation",
  };
}

const ghostBtnStyle = {
  width: "100%",
  padding: "12px",
  background: "transparent",
  color: "rgba(255,255,255,0.65)",
  border: "1px solid rgba(255,255,255,0.18)",
  borderRadius: 12,
  fontFamily: FONT.forma,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  cursor: "pointer",
  touchAction: "manipulation",
};

// ── Glyphs (inline SVG, no font/emoji dependency) ───────────────────

function CloseGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="3" y1="3" x2="11" y2="11" />
      <line x1="11" y1="3" x2="3" y2="11" />
    </svg>
  );
}

function TorchGlyph({ on }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 2h6l-1 6h2l-7 14 1-9H7l2-11z" fill={on ? "currentColor" : "none"} />
    </svg>
  );
}

function ZoomGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="6" />
      <line x1="20" y1="20" x2="15.5" y2="15.5" />
      <line x1="11" y1="8" x2="11" y2="14" />
      <line x1="8" y1="11" x2="14" y2="11" />
    </svg>
  );
}

function PhotoGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <circle cx="12" cy="13" r="3.5" />
      <path d="M8 6l1.5-2h5L16 6" />
    </svg>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

function formatBarcodeDigits(raw) {
  if (!raw || typeof raw !== "string") return raw || "";
  const d = raw.replace(/\D/g, "");
  if (d.length === 12) return `${d[0]} ${d.slice(1, 6)} ${d.slice(6, 11)} ${d[11]}`;
  if (d.length === 13) return `${d[0]} ${d.slice(1, 7)} ${d.slice(7)}`;
  if (d.length === 8)  return `${d.slice(0, 4)} ${d.slice(4)}`;
  if (d.length === 14) return `${d[0]} ${d.slice(1, 4)} ${d.slice(4, 9)} ${d.slice(9)}`;
  return d;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error("read failed"));
    reader.readAsDataURL(file);
  });
}
